// ---------------------------------------------------------------------------
// The definition of the word a screen is currently asking about.
//
// One hook per surface rather than one per word: a screen picks a word — by tap,
// by keyboard focus, or by a mouse arriving over it — and this answers for
// whichever one is picked. That is what keeps a table of forty answers from
// mounting forty subscriptions, and it is the same shape as the picked-cell
// state `EndGameScreen` already keeps for its score strip.
//
// **There is no loading state and that is deliberate.** A request still in
// flight is drawn as nothing at all: a spinner for a hover would be on screen
// longer than the thing it is waiting for, and it would flash on every one of
// the two words in three that have nothing to say.
//
// **A request that was ANSWERED with nothing is a different state as of
// 2026-08-04, and that is the one thing this hook gained.** It used to return
// `WordDefinition | null` and the null ran three cases together — in flight,
// refused, and no such entry — because all three drew nothing. The screen says
// there is no entry now (see `WordDefinition.tsx`), so `asking` and `none` have
// to be told apart here, and this is the only place in the client they are.
//
// **`none` still does not say WHY**, and that is the validity oracle rule from
// DESIGN §7 rather than an omission. `lookupDefinition` has already collapsed a
// 403, a 404, a dropped connection and a 200 carrying no gloss into one null
// before this hook sees it, so there is nothing here to leak even by accident.
// Do not add a state for a refusal: telling a refusal from an absence from
// outside the server IS the oracle.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { definitionCacheKey, lookupDefinition } from '../lib/definitions.js';
import type { WordDefinition } from '../lib/definitions.js';

export interface DefinitionRequest {
  /**
   * The id of the game the word was revealed in, or null when the surface
   * cannot name one — in which case nothing is asked and nothing is drawn.
   */
  gameId: string | null;
  /** The round's OWN index, `round.index`, not its position in an array. */
  roundIndex: number;
  word: string;
}

/**
 * What is known about the picked word.
 *
 * Three states and no fourth. `asking` is drawn as nothing, `none` is drawn as
 * the one message that covers every way there can be nothing, and `gloss` is the
 * definition. A caller that branches on more than these is a caller telling a
 * refusal from an absence.
 */
export type DefinitionAnswer =
  | { state: 'asking' }
  | { state: 'none' }
  | { state: 'gloss'; definition: WordDefinition };

/**
 * What is known about `request`, or null when nothing is being asked at all.
 *
 * The answer is tagged with the request it answers, so a slow reply for a word
 * that has since been passed over cannot be drawn under a different word. That
 * is the bug this shape exists to make impossible, and it is the same guard
 * `GameRecordScreen` puts on its own fetch.
 */
export function useWordDefinition(request: DefinitionRequest | null): DefinitionAnswer | null {
  const gameId = request?.gameId ?? null;
  const roundIndex = request?.roundIndex ?? -1;
  const word = request?.word ?? '';

  const [answer, setAnswer] = useState<{ key: string; definition: WordDefinition | null } | null>(
    null,
  );

  useEffect(() => {
    // Nothing picked, or a surface with no game id to ask against. No request
    // is made at all — this is the branch that holds mid-round, where the
    // server would refuse anyway and the client must not be the thing finding
    // that out.
    if (gameId === null || gameId.length === 0 || word.length === 0) return;

    let live = true;
    const key = definitionCacheKey(gameId, roundIndex, word);

    void lookupDefinition(gameId, roundIndex, word).then((definition) => {
      if (live) setAnswer({ key, definition });
    });

    return () => {
      live = false;
    };
  }, [gameId, roundIndex, word]);

  // Nothing picked, or nothing that could be asked: not a state, an absence of
  // one. It is what a caller draws no panel at all for.
  if (gameId === null || word.length === 0) return null;

  const key = definitionCacheKey(gameId, roundIndex, word);
  // An answer for a different word is still `asking` for this one, which is what
  // keeps a stale gloss off the screen while a new request is out.
  if (answer === null || answer.key !== key) return { state: 'asking' };
  return answer.definition === null
    ? { state: 'none' }
    : { state: 'gloss', definition: answer.definition };
}
